'use client';

import {
  type ChangeEvent,
  type FC,
  type FormEvent,
  useContext,
  useEffect,
  useCallback,
  useState,
} from 'react';
import {
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Input,
  Loading,
  Text,
} from '@/_components';
import { usePathname, useSearchParams } from 'next/navigation';
import { getFirm, getTemplate } from '@/_api/resources';
import { getPreferredSignInMethod } from '@/_api/auth';
import { LoginContext } from '@/_contexts/LoginProvider';
import { TemplateSignUpInContext } from '@/_contexts/TemplateSignUpInProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';

export const TemplateStart: FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { addToast } = useContext(ToastContext);
  const { setLoginState, setPreviousLoginState, setUsername, username } =
    useContext(LoginContext);
  const { sendMagicLink } = useContext(UserContext);

  const {
    setFirm,
    template,
    isLoadingTemplateInfo,
    setFirmGroupId,
    setTemplateRedirect,
    setIsLoadingTemplateInfo,
    setTaxYear,
    setTemplate,
    setTemplateHashId,
  } = useContext(TemplateSignUpInContext);

  const path = usePathname();
  const hashId: string | undefined = path.split('/template/')?.[1];
  const searchParams = useSearchParams();

  const _fetchTemplateInfo = useCallback(async () => {
    if (!path || !searchParams || !hashId) return;

    try {
      setIsLoadingTemplateInfo(true);
      setTemplateHashId(hashId);

      const [template, firm] = await Promise.all([
        getTemplate({ hashId }),
        getFirm({ hashId }),
      ]);

      const firmGroupId: string | null = searchParams.get('firmGroupId');
      const taxYear: string | null = searchParams.get('taxYear');

      setFirm(firm);
      setFirmGroupId(!!firmGroupId ? firmGroupId : undefined);
      setTaxYear(!!taxYear ? taxYear : undefined);
      setTemplate(template);
      setTemplateRedirect(
        `/template/${hashId}/create-questionnaire-from-template`,
      );
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoadingTemplateInfo(false);
    }
  }, [
    hashId,
    addToast,
    setTemplate,
    setTemplateRedirect,
    path,
    searchParams,
    setIsLoadingTemplateInfo,
    setTaxYear,
    setTemplateHashId,
    setFirm,
    setFirmGroupId,
  ]);

  useEffect(() => {
    _fetchTemplateInfo();
  }, [_fetchTemplateInfo]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!username) return addToast('error', 'Please enter your email');

    try {
      setIsLoading(true);

      const preferredSignInMethod = await getPreferredSignInMethod({
        username: username.trim().toLowerCase(),
      });

      if (!preferredSignInMethod) {
        setPreviousLoginState('template-start');
        setLoginState('template-sign-up');
        return;
      }

      if (preferredSignInMethod.passwordless) {
        const params = new URLSearchParams(globalThis.location.search);
        const redirect = params.get('redirect');
        sendMagicLink(
          preferredSignInMethod.accountant_id,
          redirect,
          preferredSignInMethod.uid,
        );
        setLoginState('sent-link');
      } else {
        setLoginState('login');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onUsernameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value?.trim()?.toLowerCase());
  };

  // TODO:
  // * don't need setPreviousLoginState maybe
  // * ensure back buttons work correctly once set-sign-in-method is hit
  return (
    <form onSubmit={onSubmit}>
      <Flex isColumn={true} gap={8} padding={10}>
        {isLoadingTemplateInfo ? (
          <Loading size={24} />
        ) : (
          <>
            <Heading level={5}>{template?.name}</Heading>

            <Text type="small" color="secondary">
              Before you start, please fill out the following information so we
              can save your progress.
            </Text>

            <Input
              value={username}
              label="Email"
              onChange={onUsernameChange}
              placeholder="john@example.com"
              type="email"
              isDisabled={isLoading || isLoadingTemplateInfo}
              isLoading={isLoading}
            />

            <ButtonGroup isFullWidth={true}>
              <Button
                value="Continue"
                intent="primary"
                type="submit"
                isDisabled={isLoading || isLoadingTemplateInfo}
                isLoading={isLoading}
              />
            </ButtonGroup>
          </>
        )}
      </Flex>
    </form>
  );
};
